import React, { Fragment, useEffect, useRef, useState } from "react";
import { MessageCircleMore, Files, Trash, EllipsisVertical, SendHorizontal, RotateCcw } from 'lucide-react';
import { Menu, Transition } from '@headlessui/react';
import { addUTCMin, adjustDateTime, getInitials, getPractice, getSecureData,  notify, removeSecureItem, setSecureData } from "../../helpers";
import { useDocumentTitle } from '../../hooks/customDocumentTitleHook';
import { addNewMessage, fetchShoutBoxMessage, updateShoutboxMessage } from "../../services/ShoutBox";
import Skeleton from "react-loading-skeleton";

export default function Shoutbox({selectedPracticeChange}) {
  let first_name = getPractice("first_name");
  let location_id = getPractice("location_id");
  let account_id = getPractice("account_id");
  if(selectedPracticeChange !== null){
    location_id = selectedPracticeChange?.location_id;
    account_id = selectedPracticeChange?.account_id;
  }

  const [dataLoading, setDataLoading] = useState(true);
  const [isMenuVisible, setIsMenuVisible] = useState(true);
  const [utcTimeNow, setUtcTimeNow] = useState(null);
  const [messageData, setMessageData] = useState([]);
  const [enableButton, setEnableButton] = useState(false);
  const messageBoxRef = useRef(null);

  const [formData, setFormData] = useState({
    account_id:account_id,
    location_id:location_id,
    message: '',
  });

  useEffect(() => {
    const sameLocation = formData.location_id === location_id;
    const sameAccount = formData.account_id === account_id;

    if (sameLocation && sameAccount) return;

    setFormData({
      ...formData,
      location_id: location_id,
      account_id: account_id,
    })
    if (selectedPracticeChange !== null) {
      const cleanData = async () => {
        await removeSecureItem("message_fetch_id");
        setMessageData([]);
        FetchAllMessages();
      };
      cleanData();      
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location_id, account_id]);

  useEffect(() => {
    if (messageBoxRef.current) {
      messageBoxRef.current.scrollTop = messageBoxRef.current.scrollHeight;
    }
  }, [messageData]);

  const FetchAllMessages = async () => {
    let isCanceled = false;

    try {
      const _message_fetch_id = await getSecureData("message_fetch_id");
      const response = await fetchShoutBoxMessage(
        getPractice("account_id"),
        getPractice("location_id"),
        _message_fetch_id
      );
      if (response) {
        setUtcTimeNow(new Date().toISOString());
        let set_new_data = response?.data?.result;
          
        if (_message_fetch_id !== response?.data?.last_fetch_id) {
          await setSecureData("message_fetch_id", response?.data?.last_fetch_id);
          setMessageData((prevMessageData) => [
            ...prevMessageData,
            ...set_new_data,
          ]);
        }
      }
    } catch (error) {
      if (error?.code === "ERR_CANCELED" || error?.name === "CanceledError") {
        isCanceled = true;
      }
    } finally {
      if (!isCanceled) {
        setDataLoading(false);
      }
    }
  };

  useEffect(() => {
    const removeAndFetchData = async () => {
      await removeSecureItem("message_fetch_id");
      FetchAllMessages();
    };
    removeAndFetchData();
    const intervalId = setInterval(FetchAllMessages, 3000);

    return () => clearInterval(intervalId);
  }, []);

  const handelCopyMessage =  async (message) => {
    setIsMenuVisible(false);
    if(message === ''){
      notify('error', 'Something went wrong');
    } else {
      const tempTextArea = document.createElement("textarea");
      tempTextArea.value = message;
      document.body.appendChild(tempTextArea);
      tempTextArea.select();
      document.execCommand("copy");
      document.body.removeChild(tempTextArea);
      notify('success', 'Copied', 0.5);
    }
  }

  const handelRemoveMessage =  async (id, status) => {
    setIsMenuVisible(false);
    try {
      let updateData = {
        account_id: account_id,
        location_id: location_id,
        message_id: id,
        sender_name: first_name,
        is_archived: status
      };
      const result = await updateShoutboxMessage(updateData);

      if (result.data.status === 'error') {
        notify('error', result?.data?.message);
      } else {
        setMessageData(prevMessageData =>
          prevMessageData.map(message => 
            message.id === updateData.message_id
              ? { ...message, message: result?.data?.result?.message, is_archived: result?.data?.result?.is_archived }
              : message
          )
        );
        notify('success', result?.data?.message);
      }

    } catch (error) {
      notify('error', 'Something went wrong');
    }
  }

  const handleChange = (e) => {
    const { name, value } = e.target;
    if(name === 'message' && value.length < 2){
      setEnableButton(false);
    } else {
      setEnableButton(true);
    }
    setFormData({
      ...formData,
      [name]: value,
      'sender_name': first_name,
    });
  };

  const sendMessage =  async (event) => {
    event.preventDefault();

    try {
      const response = await addNewMessage(formData);
      if (response.data.status === 'error') {
        notify('error', response?.data?.message);
      } else {
        await FetchAllMessages();
  
        setFormData({
          ...formData,
          message: ''
        });
        setEnableButton(false);
        // notify('success', response?.data?.message);
      }
      
    } catch (error) {
      notify('error', 'Something went wrong');
    }
  }

  const changeOpenMenu = () => {
    if (!isMenuVisible) {
      setIsMenuVisible(true);
    }
  };

  useDocumentTitle("Shoutbox");

  return (
    <>
      <div className="docpro_page_heading_wrap">
        <h2 className="docpro_page_heading docpro_fs_24">
          <i className="docpro_blue_bg docpro_white_color">
            <MessageCircleMore width={20} height={20} />
          </i>
          Shoutbox
        </h2>
      </div>
        
      <div className="docpro_data_table_wrap">
        <div className="docpro-shoutbox relative">
          <div className='message-box' ref={messageBoxRef}>
            {!dataLoading ? (
              messageData?.map((item, index) => (
                <div key={index}>
                  {item.account_id === account_id ?
                    item.sender_name === first_name ?
                      <div className="right-msg-data text-right" >
                        <div className="shoutbox-flex">
                          <div>
                            <div className='flex'>
                              {item?.is_archived ?
                                <div className="flex-grow-3 relative">
                                  <p><i>{item.message}</i></p>
                                  <span className="docpro_tip">{adjustDateTime(item.created_at)}</span>
                                </div>
                              :
                                <div className="flex-grow-2 relative">
                                  <h6 className="mb-0 leading-5">{item.message}</h6>
                                  <span className="docpro_tip">{adjustDateTime(item.created_at)}</span>
                                </div>
                              }

                              <div className='relative'>
                                <Menu as="div" className='relative mt-1'>
                                  {item?.is_archived && utcTimeNow > addUTCMin(item.created_at) ?
                                    ''
                                  :
                                    <Menu.Button className="relative inline-flex items-center rounded-md text-sm font-semibold" onClick={changeOpenMenu}>
                                      <span className="absolute" />
                                      <span className="sr-only">View Action</span>
                                      <EllipsisVertical className="h-4 w-4 pointer" />
                                    </Menu.Button>
                                  }

                                  {isMenuVisible && (
                                    <Transition as={Fragment} >
                                      <Menu.Items className="absolute z-10 top-7 right-0 w-36 bg-gray border border-gray-200 rounded-md">
                                        <div className="py-2">
                                          {! item?.is_archived ?
                                            <>
                                              <div className='flex items-center hover:bg-gray-100 py-1 px-3 pointer no-flex-direction' onClick={()=>handelCopyMessage(item.message)}>
                                                <Files className="h-4 w-4 font-light text-sky-500" />
                                                <p className='text-sm font-semibold pl-2 text-sky-500'> Copy</p>
                                              </div>

                                              { utcTimeNow < addUTCMin(item.created_at) &&
                                                <div className='flex items-center hover:bg-gray-100 py-1 px-3 pointer no-flex-direction' onClick={()=>handelRemoveMessage(item.id, true)}>
                                                  <Trash className="h-4 w-4 font-light text-red-500" />
                                                  <p className='text-sm font-semibold pl-2 text-red-500'> Remove</p>
                                                </div>
                                              }
                                            </>
                                          :
                                            <>
                                              { utcTimeNow < addUTCMin(item.created_at) &&
                                                <div className='flex items-center hover:bg-gray-100 py-1 px-3 pointer no-flex-direction' onClick={()=>handelRemoveMessage(item.id, false)}>
                                                  <RotateCcw className="h-4 w-4 font-light text-red-500" />
                                                  <p className='text-sm font-semibold pl-2 text-red-500'> Restore</p>
                                                </div>
                                              }
                                            </>
                                          }
                                        </div>
                                      </Menu.Items>
                                    </Transition>
                                  )}
                                </Menu>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    :
                      <div className="left-msg-data" >
                        <div className="shoutbox-flex">
                          <div className="docpro_message_item_avatar  shoutbox_img_avatar">
                            {getInitials(`${item.sender_name}`)}{" "}
                            <span className="docpro_tip">{`${item.sender_name}`}</span>
                          </div>
                          <div>
                            <div className='flex'>
                              {item?.is_archived ?
                                <div className="flex-grow-3 relative">
                                  <p><i>{item.message}</i></p>
                                  <span className="docpro_tip">{adjustDateTime(item.created_at)}</span>
                                </div>
                              :
                                <>
                                  <div className="flex-grow-2 relative">
                                    <h6 className="mb-0 leading-5">{item.message}</h6>
                                    <span className="docpro_tip">{adjustDateTime(item.created_at)}</span>
                                  </div>

                                  <div className='relative'>
                                    <Menu as="div" className='relative mt-1'>
                                      <Menu.Button className="relative inline-flex items-center rounded-md text-sm font-semibold" onClick={changeOpenMenu}>
                                        <span className="absolute" />
                                        <span className="sr-only">View Action</span>
                                        <EllipsisVertical className="h-4 w-4 pointer" />
                                      </Menu.Button>

                                      {isMenuVisible && (
                                        <Transition as={Fragment} >
                                          <Menu.Items className="absolute z-10 top-9 left-0 w-36 bg-gray border border-gray-200 rounded-md">
                                            <div className="py-2">
                                              <div className=''>
                                                <div className='flex items-center hover:bg-gray-100 pt-1 pb-1 px-3 pointer' onClick={()=>handelCopyMessage(item.message)}>
                                                  <Files className="h-4 w-4 font-light text-sky-500" />
                                                  <p className='text-sm font-semibold pl-2 text-sky-500'> Copy</p>
                                                </div>

                                                {/* <div className='flex items-center hover:bg-gray-100 pt-1 pb-1 px-3 pointer' onClick={()=>handelRemoveMessage(item.id)}>
                                                  <Trash className="h-4 w-4 font-light text-red-500" />
                                                  <p className='text-sm font-semibold pl-2 text-red-500'> Remove</p>
                                                </div> */}
                                              </div>
                                            </div>
                                          </Menu.Items>
                                        </Transition>
                                      )}
                                    </Menu>
                                  </div>
                                </>
                              }
                            </div>
                          </div>
                        </div>
                      </div>
                    :
                  ''}
                </div>
              ))
            ) : (
              Array.from({ length: 10 }).map((_, index) => (
                <div key={index} className="mb-4">
                  <div className={`${index % 2 ? "left-msg-data" : "right-msg-data text-right"}`}>
                    <div className="shoutbox-flex">
                      <div className="docpro_message_item_avatar shoutbox_img_avatar" style={{ marginRight: index % 2 ? '8px' : '0', marginLeft: index % 2 ? '0' : '8px' }}>
                        <Skeleton width="2.5rem" height="2.5rem" circle={true} borderRadius="8px"></Skeleton>
                      </div>
                      <Skeleton width={`${7 + (index % 1.5)}rem`} height="2rem" borderRadius="8px"></Skeleton>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>

          <div className='absolute bottom-0 left-0 w-full bg-white' style={{ borderRadius: '0 0 12px 12px' }}>
            <hr />
            <form onSubmit={sendMessage}>
              <div className='p-3'>
                <div className='flex items-center'>
                  <input name="message" id="chat-input" autoFocus placeholder="Write your message..." autoComplete="off" value={formData.message} onChange={handleChange} className="mr-1 resize-none block w-full rounded-md docpro_form_field size_lg" style={{ height: '42px' }} />
                  <button type="submit" className="docpro_bordered_btn docpro_fw_600 docpro_filed_btn size_lg" disabled={!enableButton} style={{ padding: '10px 15px' }}><SendHorizontal className="h-5 w-5 text-white" /></button>
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>
    </>
  );
}